/**
 * Hooks de TESOURARIA/BANCOS — consultas e mutações das 7 telas.
 *
 * Segue o padrão de `usePayables`/`useBankAccounts`: useQuery + useMutation
 * com `apiClient` (shared/api/client), invalidação no success.
 *
 * As query keys são locais a esta feature (`treasury(...)`), preservando o
 * contrato de "criar somente arquivos em features/finance" — nada aqui toca
 * shared/api/query-keys.ts.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import type {
  DailyFlow,
  PaginatedResponse,
  ScheduledPayment,
  BankTariff,
  BankTariffChannel,
  BankTariffInput,
  BankTariffUpdateInput,
  MdrRate,
  MdrRateInput,
  MdrRateUpdateInput,
  SchedulePaymentInput,
  TransmitPaymentInput,
  ConfirmFinancialEntryInput,
  ConfirmFinancialEntryResult,
} from './treasuryTypes';

/* ── Query keys locais ── */

const treasuryKeys = {
  dailyFlow: (days: number) => ['finance', 'treasury', 'daily-flow', days] as const,
  payments: (params: Record<string, unknown>) => ['finance', 'treasury', 'payments', params] as const,
  bankTariffs: (channel: BankTariffChannel) => ['finance', 'treasury', 'bank-tariffs', channel] as const,
  mdrRates: (params: Record<string, unknown>) => ['finance', 'treasury', 'mdr-rates', params] as const,
};

/* ── Fluxo de caixa diário ── */

export function useDailyFlow(days = 7) {
  return useQuery({
    queryKey: treasuryKeys.dailyFlow(days),
    queryFn: () =>
      apiClient.get<DailyFlow>('/finance/flow/daily', {
        params: { days: String(days) },
      }),
  });
}

/* ── Pagamentos CNAB/API ── */

export type PaymentsFilters = {
  page?: number;
  pageSize?: number;
  statusIn?: string[];
  channel?: string;
  q?: string;
};

export function useScheduledPayments(filters: PaymentsFilters = {}) {
  const { page = 1, pageSize = 20, statusIn, channel, q } = filters;
  return useQuery({
    queryKey: treasuryKeys.payments(filters),
    queryFn: () =>
      apiClient.get<PaginatedResponse<ScheduledPayment>>('/finance/payments', {
        params: {
          page: String(page),
          pageSize: String(pageSize),
          ...(statusIn && statusIn.length > 0 ? { statusIn } : {}),
          ...(channel ? { channel } : {}),
          ...(q ? { q } : {}),
        },
      }),
  });
}

export function useSchedulePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SchedulePaymentInput) =>
      apiClient.post<ScheduledPayment>('/finance/payments', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payments'] });
    },
  });
}

export function useTransmitPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, batchId }: { id: string; batchId: string }) =>
      apiClient.post<ScheduledPayment>(`/finance/payments/${id}/transmit`, {
        batchId,
      } satisfies TransmitPaymentInput),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payments'] });
    },
  });
}

export function useConfirmFinancialEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ConfirmFinancialEntryInput }) =>
      apiClient.post<ConfirmFinancialEntryResult>(`/finance/payments/${id}/confirm`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payments'] });
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'daily-flow'] });
    },
  });
}

/* ── Tarifas bancárias ── */

export function useBankTariffs(channel: BankTariffChannel) {
  return useQuery({
    queryKey: treasuryKeys.bankTariffs(channel),
    queryFn: () =>
      apiClient.get<PaginatedResponse<BankTariff>>('/finance/bank-tariffs', {
        params: { channel, page: '1', pageSize: '200' },
      }),
  });
}

export function useCreateBankTariff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BankTariffInput) =>
      apiClient.post<BankTariff>('/finance/bank-tariffs', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'bank-tariffs'] });
    },
  });
}

export function useUpdateBankTariff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & BankTariffUpdateInput) =>
      apiClient.put<BankTariff>(`/finance/bank-tariffs/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'bank-tariffs'] });
    },
  });
}

export function useDeleteBankTariff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ id: string; deleted: boolean }>(`/finance/bank-tariffs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'bank-tariffs'] });
    },
  });
}

/* ── MDR — taxas de operadoras ── */

export function useMdrRates(filters: { brand?: string; isActive?: boolean } = {}) {
  return useQuery({
    queryKey: treasuryKeys.mdrRates(filters),
    queryFn: () =>
      apiClient.get<PaginatedResponse<MdrRate>>('/finance/mdr-rates', {
        params: {
          page: '1',
          pageSize: '200',
          ...(filters.brand ? { brand: filters.brand } : {}),
          ...(filters.isActive !== undefined ? { isActive: String(filters.isActive) } : {}),
        },
      }),
  });
}

export function useCreateMdrRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MdrRateInput) =>
      apiClient.post<MdrRate>('/finance/mdr-rates', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'mdr-rates'] });
    },
  });
}

export function useUpdateMdrRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & MdrRateUpdateInput) =>
      apiClient.put<MdrRate>(`/finance/mdr-rates/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'mdr-rates'] });
    },
  });
}

export function useDeleteMdrRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ id: string; deleted: boolean }>(`/finance/mdr-rates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'mdr-rates'] });
    },
  });
}